// Detect gift-recorder clips inside the receiver's video library.
//
// The sender's recorder names each clip after the prompt's `subject`
// (see GiftRecorder.jsx → filename = `${safeSubject}.${ext}`). On the
// receiver side we walk through the user's videos and pick the ones
// whose title matches a known subject. Matches are grouped by occasion
// and ordered by the prompt order so playback feels like a guided story.
import { occasionQuestions } from '../gift-flow/occasionQuestions';

const normalize = (s) => (s || '').toString().trim().toLowerCase();

// Build a flat lookup: subjectKey -> { occasion, order, original }.
// Cache on first call since the source is static.
let subjectIndex = null;
const buildSubjectIndex = () => {
  if (subjectIndex) return subjectIndex;
  const idx = new Map();
  Object.entries(occasionQuestions).forEach(([occasion, prompts]) => {
    prompts.forEach((p, order) => {
      const key = normalize(p.subject);
      if (!idx.has(key)) idx.set(key, []);
      idx.get(key).push({ occasion, order, original: p.subject });
    });
  });
  subjectIndex = idx;
  return idx;
};

/**
 * Find gift-recorder clips in a video list.
 * @param {Array<{id, title, thumbnail, ...}>} videos  Video list from getMyContents.
 * @returns {{
 *   clips: Array<{id, title, thumbnail, subject, order}>,
 *   occasion: string|null,
 *   total: number  // count of clips found in the chosen occasion
 * }}
 */
export const findGiftMessageClips = (videos) => {
  if (!Array.isArray(videos) || videos.length === 0) {
    return { clips: [], occasion: null, total: 0 };
  }
  const idx = buildSubjectIndex();

  // For every video, see if its title matches any subject. A subject can
  // belong to multiple occasions (rare but possible if we ever overlap
  // copy), so collect candidates per occasion.
  const candidatesByOccasion = new Map();
  videos.forEach((v) => {
    const titleKey = normalize(v.title);
    const matches = idx.get(titleKey);
    if (!matches) return;
    matches.forEach((m) => {
      if (!candidatesByOccasion.has(m.occasion)) candidatesByOccasion.set(m.occasion, []);
      candidatesByOccasion.get(m.occasion).push({
        ...v,
        subject: m.original,
        order: m.order,
      });
    });
  });

  if (candidatesByOccasion.size === 0) {
    return { clips: [], occasion: null, total: 0 };
  }

  // Pick the occasion with the most matches. Stable: ties broken by
  // occasion key alphabetical to keep the result deterministic.
  let chosenOccasion = null;
  let chosenClips = [];
  candidatesByOccasion.forEach((clips, occasion) => {
    if (clips.length > chosenClips.length
      || (clips.length === chosenClips.length && (chosenOccasion === null || occasion < chosenOccasion))) {
      chosenOccasion = occasion;
      chosenClips = clips;
    }
  });

  // Dedupe by clip id (a video could repeat if the user uploaded it twice)
  // and sort by the prompt order so playback follows the recording sequence.
  const seen = new Set();
  const ordered = chosenClips
    .filter((c) => {
      if (seen.has(c.id)) return false;
      seen.add(c.id);
      return true;
    })
    .sort((a, b) => a.order - b.order);

  return { clips: ordered, occasion: chosenOccasion, total: ordered.length };
};
